import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Heart, CreditCard, Loader2, CheckCircle, Coins } from 'lucide-react';
import { useFootprint } from '../hooks/useFootprint';
import { Capacitor } from '@capacitor/core';

// Initialize Stripe with Public Key
const stripePromise = loadStripe('pk_live_51T0qQ08zTRoiYcJ5qy8Xd9OAFfq9tVWcfIIuBX8FJFCU2wQc1PdBauoPMDWlinSIWFNpGmNBcJT7qHpSA8X2m2dC00tfxekRq6');

// Use full URL for Native App OR Dev, relative for Web Prod
const API_URL = Capacitor.isNativePlatform() || import.meta.env.DEV 
  ? 'https://habl.life/donation_api.php' 
  : '/donation_api.php';

function CheckoutForm({ amount, onSuccess }: { amount: number, onSuccess: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const { mintCoin } = useFootprint(); // Use mintCoin

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href, // Returns to same page
      },
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message || 'An error occurred');
      setProcessing(false);
    } else {
      // Payment successful!
      // Mint Habl Coins as a reward for donation (e.g. 10 coins per £1)
      const coinsToMint = Math.floor(amount / 100) * 10; 
      await mintCoin('zakat', `Donation of £${(amount/100).toFixed(2)}`, coinsToMint);
      
      onSuccess();
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && <div className="text-red-500 text-sm">{error}</div>}
      <button 
        type="submit" 
        disabled={!stripe || processing}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
        {processing ? 'Processing...' : `Pay £${(amount/100).toFixed(2)}`}
      </button>
    </form>
  );
}

export function DonationForm() {
  const [amount, setAmount] = useState(1000); // Default £10.00 (in pence)
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Calculate potential coin reward
  const rewardCoins = Math.floor(amount / 100) * 10;

  const handleDonate = async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      console.log("Fetching payment intent from:", API_URL);
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount }),
      });
      
      if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
      }

      const data = await res.json();
      if (data.client_secret) {
        setClientSecret(data.client_secret);
      } else {
        console.error('No client secret returned', data);
        setErrorMessage('Failed to initialize payment. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching payment intent:', err);
      setErrorMessage('Connection error. Please check your internet or try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-8 text-center space-y-4">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-emerald-900">Thank You!</h3>
        <p className="text-emerald-700">Your donation has been received. May Allah reward you.</p>
        
        <div className="bg-white/50 p-4 rounded-xl inline-flex items-center gap-2 border border-emerald-200">
             <Coins className="w-5 h-5 text-amber-500" />
             <span className="font-bold text-emerald-800">+{Math.floor(amount / 100) * 10} Habl Coins Minted</span>
        </div>

        <button onClick={() => setSuccess(false)} className="text-sm text-emerald-600 underline block mx-auto mt-2">Donate again</button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6 shadow-sm">
      <div className="text-center space-y-2">
        <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center mx-auto">
          <Heart className="w-6 h-6 text-rose-500 fill-rose-500" />
        </div>
        <h3 className="text-lg font-bold text-slate-900">Make a Donation</h3>
        <p className="text-sm text-slate-500">Secure payments via Stripe</p>
      </div>

      {!clientSecret ? (
        <div className="space-y-4">
          <label className="block text-sm font-medium text-slate-700">Select Amount</label>
          <div className="grid grid-cols-3 gap-2">
            {[500, 1000, 2000, 5000].map((val) => (
              <button
                key={val}
                onClick={() => setAmount(val)}
                className={`py-2 px-4 rounded-lg text-sm font-semibold transition-colors flex flex-col items-center gap-1 ${
                  amount === val 
                    ? 'bg-emerald-600 text-white shadow-md' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>£{(val/100).toFixed(0)}</span>
                <span className={`text-[10px] font-mono ${amount === val ? 'text-emerald-200' : 'text-slate-400'}`}>
                    +{(val/100) * 10} Coins
                </span>
              </button>
            ))}
            <div className="col-span-2 relative">
               <span className="absolute left-3 top-3 text-slate-400">£</span>
               <input 
                 type="number" 
                 className="w-full h-full bg-slate-50 border border-slate-200 rounded-lg py-2 pl-6 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                 placeholder="Other"
                 onChange={(e) => {
                   const val = parseFloat(e.target.value);
                   if (val > 0) setAmount(Math.round(val * 100));
                 }}
               />
            </div>
          </div>
          
          {amount > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 flex items-center justify-center gap-2 text-xs text-amber-800">
                  <Coins className="w-3 h-3" />
                  You will receive <strong>{rewardCoins} Habl Coins</strong> as a token of gratitude.
              </div>
          )}

          {errorMessage && (
              <div className="bg-red-50 border border-red-100 text-red-600 text-sm p-3 rounded-lg text-center">
                  {errorMessage}
              </div>
          )}

          <button 
            onClick={handleDonate}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all mt-4"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue to Payment'}
          </button>
        </div>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
          <CheckoutForm amount={amount} onSuccess={() => setSuccess(true)} />
        </Elements>
      )}
    </div>
  );
}